<section class="cost-estimate-psl">
  <div class="cost-estimate-body">
    <div class="cost-estimate-card" [class.has-panel]="isEstimateRequested()">
      <div class="cost-estimate-header">
        @if (!isEstimateRequested()) {
          <p class="eyebrow">Get an estimate</p>
        }

        <h2 class="headline" data-test="cost-estimate-headline">
          @if (isEstimateRequested()) {
            Your estimate<sup>{{ variant() }}</sup>
          } @else {
            @switch (variant()) {
              @case (costEstimateVariants.COMMON_COPAY) {
                The most common copay is $25 a session<sup>1</sup>
              }
              @case (costEstimateVariants.INSURANCE_ESTIMATE) {
                Here's your estimate<sup>2</sup>
              }
              @default {
                Here's your estimate<sup>3</sup>
              }
            }
          }
        </h2>
      </div>

      <form
        class="cost-estimate-filters"
        [formGroup]="form"
        (ngSubmit)="onSubmit()"
      >
        <div class="cost-estimate-filter">
          <label class="field-label" id="state-label" for="state-select">What state do you live in?*</label>
          <filter-select
            formControlName="state"
            placeholder="State"
            ariaLabelledBy="state-label"
            [categorizedOptions]="stateOptions()"
            [isMulti]="false"
            [isSearchable]="stateOptions().length > 5"
            [customErrorMessages]="{ required: 'Required' }"
            [matchFieldWidth]="true"
            data-test="cost-estimate-state"
          ></filter-select>
        </div>

        <div class="cost-estimate-filter">
          <label class="field-label" id="insurance-label" for="insurance-select">How do you plan to cover your session?*</label>
          <filter-select
            formControlName="insurance"
            placeholder="Insurance"
            ariaLabelledBy="insurance-label"
            [categorizedOptions]="insuranceOptions()"
            [emptyOptionText]="insuranceEmptyText()"
            [isMulti]="false"
            [isSearchable]="true"
            [customErrorMessages]="{ required: 'Required' }"
            [matchFieldWidth]="true"
            data-test="cost-estimate-insurance"
          ></filter-select>
        </div>

        @if (isEstimateRequested()) {
          <p class="update-hint" data-test="cost-estimate-update-hint">
            <iris-icon
              name="refresh"
              [size]="iconSizes.SM"
              colorToken="$color-neutral-80"
            ></iris-icon>
            Change any answer above to update your estimate
          </p>
        } @else {
          <div class="cost-estimate-submit-group">
            <iris-button
              class="submit-button"
              [buttonType]="irisButtonTypes.PRIMARY"
              [typeAttribute]="irisButtonTypeAttributes.SUBMIT"
              data-test="cost-estimate-submit"
            >
              Get estimate
            </iris-button>

            <p class="find-provider-hint">
              Don't see your state or insurance?
              <a
                class="browse-link"
                data-test="cost-estimate-find-another"
                [href]="browseOtherProvidersUrl()"
              >Find another provider</a>
            </p>
          </div>
        }
      </form>
    </div>

    @if (estimate(); as costEstimate) {
      <div class="estimate-panel" data-test="cost-estimate-result">
        <p class="estimate-amount">
          <span class="estimate-cost" data-test="cost-estimate-amount">{{ costEstimate.formattedCost }}</span>
          <span class="estimate-cadence">per 60-minute session</span>
        </p>

        @if (costEstimate.isSelfPay) {
          <p class="estimate-disclaimer" data-test="cost-estimate-disclaimer">
            This is our standard self-pay rate for {{ costEstimate.service }} in
            {{ costEstimate.stateName }}, no insurance required. Your final cost may vary a bit by
            provider and session length.
          </p>
        } @else {
          <p class="estimate-disclaimer" data-test="cost-estimate-disclaimer">
            SonderMind accepts {{ costEstimate.insuranceTitle }} for {{ costEstimate.service }} in
            {{ costEstimate.stateName }}. Your copay is set by your insurance, not by SonderMind, so
            you'd pay the same with any in-network provider. We just believe in showing it to you up
            front. Your exact amount depends on your plan.
          </p>
        }

        <div class="estimate-actions">
          <iris-button
            class="schedule-session-button"
            [buttonType]="irisButtonTypes.PRIMARY"
            [typeAttribute]="irisButtonTypeAttributes.BUTTON"
            data-test="cost-estimate-schedule"
            (click)="onScheduleSessionClicked()"
          >
            Schedule a session
          </iris-button>

          <a class="browse-link" data-test="cost-estimate-browse" [href]="browseOtherProvidersUrl()">
            Browse other providers
          </a>
        </div>
      </div>
    } @else if (isEstimateRequested()) {
      <div class="estimate-panel" data-test="cost-estimate-unavailable">
        <p class="estimate-empty">We're unable to provide an estimate at this time</p>
      </div>
    }
  </div>

  <p class="footnote" data-test="cost-estimate-footnote">
    @switch (variant()) {
      @case (costEstimateVariants.SELF_PAY_ESTIMATE) {
        <sup>3</sup>This is our standard self-pay rate based on contracted provider rates from
        January-December 2025. Your final cost may vary by provider, session length, and service type.
        This is not a binding quote.
      }
      @default {
        <!-- Variants 1 and 2 share this disclaimer; only the marker differs. -->
        <sup>{{ variant() }}</sup>$25 is the most common per-session copay among SonderMind clients
        using insurance, based on claims from January-December 2025. Your actual cost may be higher.
        Copays are set by your insurance plan, not SonderMind, and depend on your plan, deductible, and
        out-of-pocket maximum. This is not a good faith estimate or insurance authorization. Contact
        your insurer to confirm your cost.
      }
    }
  </p>
</section>
